import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Search, Building2, ChevronRight, Mail, Phone, MapPin, ClipboardList, Pencil } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DatePickerIT } from "../../components/DatePickerIT";
import OrderRowActions from "../../components/OrderRowActions";
import { ChatDrawer } from "../../components/ChatDrawer";
import { ChangeCustomerDialog } from "../../components/ChangeCustomerDialog";
import { OrderEditModal } from "../../portals/WarehousePortal";
import { OrderOriginalCompare } from "../../components/OrderOriginalCompare";
import { API } from "../../utils/format";
import { formatMoney } from "../../utils/currency";
import { useLang } from "../../context/LangContext";
import { useCompany } from "../../context/CompanyContext";

const OrdersManagement = () => {
  const { t } = useLang();
  const { company, displayMode } = useCompany();
  const fm = (v) => formatMoney(v, company, { displayMode });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [chatOrderId, setChatOrderId] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Auto-open order detail from ?openOrderId=... / ?openOrderNumber=... (deep-link from chat)
  useEffect(() => {
    if (!orders.length) return;
    const params = new URLSearchParams(location.search);
    const openId = params.get("openOrderId");
    const openNum = params.get("openOrderNumber");
    if (!openId && !openNum) return;
    const found = orders.find(o => o.id === openId || o.order_number === openNum || o.order_number === openId);
    if (found) setSelected(found);
  }, [orders, location.search]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.append("status", filter);
      if (search) params.append("search", search);
      const res = await axios.get(`${API}/orders?${params}`);
      setOrders(res.data || []);
    } catch (e) {
      toast.error("Errore caricamento ordini");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchOrders, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, search]);

  const updateStatus = async (id, status) => {
    try {
      // Special handling for cancellation: ask reason and warn if order had matured commission
      if (status === "cancelled") {
        const ord = selected?.id === id ? selected : orders.find(o => o.id === id);
        const commission = parseFloat(ord?.commission_amount || 0);
        const wasDeliveredLike = ["delivered", "completed", "shipped", "ready"].includes(ord?.status);
        const baseMsg = wasDeliveredLike && commission > 0
          ? `⚠️ Questo ordine ha già maturato una provvigione di ${fm(commission)} per l'agente ${ord?.agent_name || ""}.\n\nAnnullando l'ordine la provvigione verrà AUTOMATICAMENTE STORNATA.\n\nMotivo dell'annullamento (es. reso cliente):`
          : "Sei sicuro di voler annullare questo ordine?\n\nMotivo dell'annullamento (opzionale):";
        const reason = window.prompt(baseMsg, wasDeliveredLike ? "Reso cliente" : "");
        if (reason === null) return; // user cancelled
        const r = await axios.patch(`${API}/orders/${id}/status`, { status, cancellation_reason: reason });
        const voided = r.data?.voided_commission || 0;
        toast.success(voided > 0 ? `Ordine annullato — provvigione di ${fm(voided)} stornata` : "Ordine annullato");
        fetchOrders();
        if (selected?.id === id) setSelected({ ...selected, status, cancellation_reason: reason, cancelled_after_delivery: wasDeliveredLike, commission_voided_amount: voided });
        return;
      }
      await axios.patch(`${API}/orders/${id}/status`, { status });
      toast.success("Stato aggiornato");
      fetchOrders();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo ordine?")) return;
    try {
      await axios.delete(`${API}/orders/${id}`);
      toast.success("Ordine eliminato");
      setSelected(null);
      setCheckedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
      fetchOrders();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  // ---- Eliminazione multipla ----
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [changeCustOpen, setChangeCustOpen] = useState(false);
  const toggleChecked = (id) => setCheckedIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => setCheckedIds((prev) => prev.size === orders.length ? new Set() : new Set(orders.map((o) => o.id)));
  const bulkDelete = async () => {
    if (!window.confirm(`Eliminare definitivamente ${checkedIds.size} ordini? L'operazione è irreversibile.`)) return;
    try {
      const r = await axios.post(`${API}/orders/bulk-delete`, { order_ids: Array.from(checkedIds) });
      toast.success(`${r.data.deleted} ordini eliminati`);
      setCheckedIds(new Set());
      setSelected(null);
      fetchOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione multipla");
    }
  };

  const statusColor = (s) => ({
    pending: "bg-yellow-100 text-yellow-700",
    confirmed: "bg-blue-100 text-blue-700",
    in_preparation: "bg-orange-100 text-orange-700",
    ready: "bg-cyan-100 text-cyan-700",
    shipped: "bg-purple-100 text-purple-700",
    delivered: "bg-emerald-100 text-emerald-700",
    completed: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
  })[s] || "bg-stone-100 text-stone-700";

  const statusLabel = (s) => ({
    pending_agent: t.statusPendingAgent, pending: t.statusPending, confirmed: t.statusConfirmed, in_preparation: t.statusInPrep,
    ready: t.statusReady, shipped: t.statusShipped, delivered: t.statusDelivered,
    completed: t.statusCompleted, cancelled: t.statusCancelled
  })[s] || s;

  const [paymentForm, setPaymentForm] = useState({ method: "bonifico", amount: 0, date: new Date().toISOString().slice(0, 10), note: "" });
  const refreshSelected = async (id) => {
    try {
      const r = await axios.get(`${API}/orders/${id}`);
      setSelected(r.data);
    } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
  };
  const addPayment = async () => {
    if (!paymentForm.amount || paymentForm.amount <= 0) { toast.error("Importo obbligatorio"); return; }
    try {
      await axios.post(`${API}/orders/${selected.id}/payments`, paymentForm);
      toast.success("Pagamento registrato");
      setPaymentForm({ method: "bonifico", amount: 0, date: new Date().toISOString().slice(0, 10), note: "" });
      refreshSelected(selected.id);
      fetchOrders();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const removePayment = async (pid) => {
    try {
      await axios.delete(`${API}/orders/${selected.id}/payments/${pid}`);
      toast.success("Pagamento eliminato");
      refreshSelected(selected.id);
      fetchOrders();
    } catch { toast.error("Errore"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-display font-semibold text-stone-900">{t.ordersList}</h2>
          <p className="text-stone-500 text-sm mt-1">{orders.length} — {t.customers}</p>
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder={t.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-stone-300"
            data-testid="orders-search"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-white border-stone-300" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.all}</SelectItem>
            <SelectItem value="pending_agent">⏳ {t.statusPendingAgent}</SelectItem>
            <SelectItem value="pending">{t.statusPending}</SelectItem>
            <SelectItem value="confirmed">{t.statusConfirmed}</SelectItem>
            <SelectItem value="shipped">{t.statusShipped}</SelectItem>
            <SelectItem value="completed">{t.statusCompleted}</SelectItem>
            <SelectItem value="cancelled">{t.statusCancelled}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {checkedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-3 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="bulk-delete-bar">
          <p className="text-sm text-stone-700"><b>{checkedIds.size}</b> ordini selezionati</p>
          <Button size="sm" onClick={bulkDelete} className="bg-red-600 hover:bg-red-700 text-white h-8" data-testid="bulk-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina selezionati
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setCheckedIds(new Set())} className="h-8 text-stone-500">Annulla selezione</Button>
        </div>
      )}

      <Card className="bg-white border-stone-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
        ) : orders.length === 0 ? (
          <div className="p-12 text-center text-stone-500">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p>Nessun ordine ricevuto</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="p-3 w-8">
                  <input
                    type="checkbox"
                    checked={orders.length > 0 && checkedIds.size === orders.length}
                    onChange={toggleAll}
                    className="w-4 h-4 accent-[#8A1C2A] cursor-pointer"
                    data-testid="orders-check-all"
                  />
                </th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">N°</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">{t.dateCol}</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">{t.customerCol}</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">{t.agentCol}</th>
                <th className="text-center p-3 text-xs font-semibold text-stone-600">{t.itemsCol}</th>
                <th className="text-right p-3 text-xs font-semibold text-stone-600">{t.totalCol}</th>
                <th className="text-center p-3 text-xs font-semibold text-stone-600">{t.status}</th>
                <th className="text-right p-3 text-xs font-semibold text-stone-600">{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer"
                  onClick={() => setSelected(o)}
                  data-testid={`order-row-${o.order_number}`}
                >
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={checkedIds.has(o.id)}
                      onChange={() => toggleChecked(o.id)}
                      className="w-4 h-4 accent-[#8A1C2A] cursor-pointer"
                      data-testid={`order-check-${o.order_number}`}
                    />
                  </td>
                  <td className="p-3 text-sm font-mono font-semibold text-[#8A1C2A]">{o.order_number}</td>
                  <td className="p-3 text-xs text-stone-600">{new Date(o.created_at).toLocaleString('it-IT')}</td>
                  <td className="p-3 text-sm">
                    <div className="font-medium text-stone-900">{o.customer?.customer_name}</div>
                    <div className="text-xs text-stone-500">{o.customer?.company}</div>
                  </td>
                  <td className="p-3 text-xs text-stone-600">{o.agent_name || "-"}</td>
                  <td className="p-3 text-center text-sm">{o.items?.length || 0}</td>
                  <td className="p-3 text-right">
                    <div className="font-semibold text-[#8A1C2A]">{fm((o.final_grand_total ?? o.grand_total) || o.total_amount || 0)} <span className="text-[9px] font-normal text-stone-400">IVA incl.</span></div>
                    <div className="text-xs text-stone-500">{fm((o.final_subtotal_amount ?? o.subtotal_amount ?? o.total_amount) || 0)} <span className="text-[9px] text-stone-400">IVA escl.</span></div>
                  </td>
                  <td className="p-3 text-center">
                    <Badge className={statusColor(o.status)}>{statusLabel(o.status)}</Badge>
                  </td>
                  <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <OrderRowActions
                        order={o}
                        onOpenDetail={(ord) => setSelected(ord)}
                        onOpenChat={(id) => setChatOrderId(id)}
                        showApprove={true}
                        onApproved={fetchOrders}
                        emailEnabled
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditOrder(o)}
                        className="border-[#8A1C2A]/40 text-[#8A1C2A] hover:bg-[#8A1C2A]/10 h-8 px-2"
                        title="Modifica ordine (righe, pesi, prezzi, U.M.)"
                        data-testid={`edit-order-row-${o.order_number}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteOrder(o.id)}
                        className="border-red-300 text-red-500 hover:bg-red-50 h-8 px-2"
                        title="Elimina ordine"
                        data-testid={`delete-order-row-${o.order_number}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      {/* Order Details Modal — full-screen su mobile per scroll verticale corretto */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-3xl bg-white p-4 sm:p-6 h-[100dvh] max-h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto rounded-none sm:rounded-lg" data-testid="order-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <span className="text-stone-900">Ordine</span>
                  <span className="font-mono text-[#8A1C2A] text-sm sm:text-base">{selected.order_number}</span>
                  <Badge className={statusColor(selected.status)}>{statusLabel(selected.status)}</Badge>
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <Card className="bg-stone-50 border-stone-200 p-3">
                  <p className="text-xs font-semibold text-stone-500 uppercase mb-2">Cliente</p>
                  <p className="font-semibold text-stone-900">{selected.customer?.customer_name}</p>
                  {selected.customer?.company && <p className="text-stone-600"><Building2 className="w-3 h-3 inline mr-1" />{selected.customer.company}</p>}
                  {selected.customer?.vat_number && <p className="text-xs text-stone-500">P.IVA/CF: {selected.customer.vat_number}</p>}
                  <p className="text-stone-700 mt-2"><Mail className="w-3 h-3 inline mr-1" />{selected.customer?.email}</p>
                  <p className="text-stone-700"><Phone className="w-3 h-3 inline mr-1" />{selected.customer?.phone}</p>
                  <p className="text-stone-700"><MapPin className="w-3 h-3 inline mr-1" />{selected.customer?.address} {selected.customer?.city}</p>
                  {selected.customer_id && (
                    <button
                      onClick={() => { setSelected(null); navigate(`/admin?tab=statements&customer_id=${selected.customer_id}`); }}
                      className="mt-2 text-xs text-[#8A1C2A] underline font-semibold text-left"
                      data-testid="admin-order-customer-statements-link"
                    >
                      📊 Situazione contabile cliente →
                    </button>
                  )}
                </Card>
                <Card className="bg-stone-50 border-stone-200 p-3">
                  <p className="text-xs font-semibold text-stone-500 uppercase mb-2">Info Ordine</p>
                  <p><span className="text-stone-500">Data:</span> {new Date(selected.created_at).toLocaleString('it-IT')}</p>
                  <p><span className="text-stone-500">Agente:</span> {selected.agent_name || "-"}</p>
                  {selected.requested_shipping_date && <p><span className="text-stone-500">🚚 Consegna richiesta:</span> <strong className="text-amber-700">{new Date(selected.requested_shipping_date).toLocaleDateString('it-IT')}</strong></p>}
                  <p><span className="text-stone-500">Codice:</span> <span className="font-mono">{selected.agent_code}</span></p>
                  {selected.document_reference && <p><span className="text-stone-500">Rif. DDT:</span> {selected.document_reference}</p>}
                  {selected.ddt_attachment?.path && (
                    <button
                      onClick={() => window.open(`${API}/files/${selected.ddt_attachment.path}`, "_blank")}
                      className="mt-1 text-xs text-[#8A1C2A] underline text-left"
                      data-testid="admin-order-ddt-attachment"
                    >
                      📎 DDT originale: {selected.ddt_attachment.filename || "apri allegato"}
                    </button>
                  )}
                  <p className="mt-2"><span className="text-stone-500">Imponibile:</span> <strong>{fm((selected.final_subtotal_amount ?? selected.subtotal_amount ?? selected.total_amount) || 0)}</strong></p>
                  <p><span className="text-stone-500">IVA:</span> {fm((selected.final_vat_total ?? selected.vat_total) || 0)}</p>
                  <p><span className="text-stone-500">Totale IVA incl.:</span> <strong className="text-[#8A1C2A]">{fm((selected.final_grand_total ?? selected.grand_total ?? selected.total_amount) || 0)}</strong></p>
                </Card>
              </div>

              {selected.customer?.special_requests && (
                <div className="bg-[#C5A059]/10 border-l-4 border-[#C5A059] p-3 rounded">
                  <p className="text-xs font-semibold text-stone-700 mb-1">Richieste speciali</p>
                  <p className="text-sm text-stone-700 whitespace-pre-wrap">{selected.customer.special_requests}</p>
                </div>
              )}

              {selected.delivery_notes && (
                <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded" data-testid="admin-order-delivery-notes">
                  <p className="text-xs font-semibold text-amber-800 mb-1">📝 Note ordine / consegna</p>
                  <p className="text-sm text-stone-800 whitespace-pre-wrap">{selected.delivery_notes}</p>
                </div>
              )}
              {selected.warehouse_notes && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded" data-testid="admin-order-warehouse-notes">
                  <p className="text-xs font-semibold text-blue-800 mb-1">🏭 Note magazzino</p>
                  <p className="text-sm text-stone-800 whitespace-pre-wrap">{selected.warehouse_notes}</p>
                </div>
              )}
              {selected.internal_notes && (
                <div className="bg-stone-100 border-l-4 border-stone-400 p-3 rounded" data-testid="admin-order-internal-notes">
                  <p className="text-xs font-semibold text-stone-700 mb-1">🔒 Note interne</p>
                  <p className="text-sm text-stone-800 whitespace-pre-wrap">{selected.internal_notes}</p>
                </div>
              )}

              <Card className="bg-white border-stone-200 min-h-fit">
                {/* Mobile: card per articolo (la tabella collassa su schermi piccoli) */}
                <div className="sm:hidden" data-testid="order-items-mobile">
                  <div className="divide-y divide-stone-100">
                    {selected.items?.map((it, idx) => {
                      const q = it.actual_quantity ?? it.quantity;
                      const lt = it.actual_line_total ?? it.line_total;
                      return (
                        <div key={idx} className="p-3">
                          <p className="text-[10px] font-mono text-stone-500">{it.code}</p>
                          <p className="text-sm font-medium text-stone-900">{it.description}</p>
                          <div className="flex justify-between items-center text-xs mt-1">
                            <span className="text-stone-600">{q} {it.unit} × {fm(it.unit_price)}</span>
                            <span className="font-semibold text-[#8A1C2A]">{fm(lt || 0)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="p-3 bg-stone-50 border-t border-stone-200 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-stone-600">Imponibile</span><span className="font-semibold">{fm((selected.final_subtotal_amount ?? selected.subtotal_amount ?? selected.total_amount) || 0)}</span></div>
                    <div className="flex justify-between"><span className="text-stone-600">IVA {selected.customer?.is_foreign ? "(estero 0%)" : ""}</span><span>{fm((selected.final_vat_total ?? selected.vat_total) || 0)}</span></div>
                    <div className="flex justify-between text-base pt-1 border-t border-stone-200"><span className="font-semibold">Totale ordine</span><span className="font-bold text-[#8A1C2A]">{fm((selected.final_grand_total ?? selected.grand_total ?? selected.total_amount) || 0)}</span></div>
                  </div>
                </div>
                <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm min-w-[520px]">
                  <thead className="bg-stone-100">
                    <tr>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Codice</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Descrizione</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Qta</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">{company?.currency_symbol || "€"} / U.M.</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.items?.map((it, idx) => (
                      <tr key={idx} className="border-t border-stone-100">
                        <td className="p-2 font-mono text-xs">{it.code}</td>
                        <td className="p-2">{it.description}</td>
                        <td className="p-2 text-right">{it.actual_quantity ?? it.quantity} {it.unit}</td>
                        <td className="p-2 text-right">{fm(it.unit_price)}</td>
                        <td className="p-2 text-right font-semibold text-[#8A1C2A]">{fm((it.actual_line_total ?? it.line_total) || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-stone-50 border-t-2 border-stone-200">
                      <td colSpan="4" className="p-3 text-right font-semibold">Imponibile</td>
                      <td className="p-3 text-right font-semibold">{fm((selected.final_subtotal_amount ?? selected.subtotal_amount ?? selected.total_amount) || 0)}</td>
                    </tr>
                    <tr>
                      <td colSpan="4" className="px-3 py-1 text-right text-sm">IVA {selected.customer?.is_foreign ? "(estero 0%)" : ""}</td>
                      <td className="px-3 py-1 text-right text-sm">{fm((selected.final_vat_total ?? selected.vat_total) || 0)}</td>
                    </tr>
                    <tr className="border-t border-stone-300">
                      <td colSpan="4" className="p-3 text-right font-semibold">Totale ordine</td>
                      <td className="p-3 text-right text-lg font-bold text-[#8A1C2A]">{fm((selected.final_grand_total ?? selected.grand_total ?? selected.total_amount) || 0)}</td>
                    </tr>
                  </tfoot>
                </table>
                </div>
              </Card>

              {/* Confronto ordine originale vs spedito */}
              <OrderOriginalCompare order={selected} fm={fm} />

              {/* Pagamenti */}
              <Card className="bg-white border-stone-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-stone-900 flex items-center gap-2">💰 Pagamenti</h4>
                  {selected.is_fully_paid ? (
                    <Badge className="bg-green-100 text-green-700">Saldato</Badge>
                  ) : (
                    <Badge className="bg-orange-100 text-orange-700">
                      Saldato: {fm(selected.paid_amount || 0)} / {fm((selected.final_grand_total ?? selected.grand_total) || 0)}
                    </Badge>
                  )}
                </div>
                {(selected.payments || []).length === 0 ? (
                  <p className="text-sm text-stone-500 italic mb-3">Nessun pagamento registrato</p>
                ) : (
                  <div className="space-y-1 mb-3">
                    {(selected.payments || []).map(p => (
                      <div key={p.id} className="flex items-center justify-between text-sm bg-stone-50 p-2 rounded" data-testid={`payment-row-${p.id}`}>
                        <div>
                          <span className="font-semibold text-[#8A1C2A]">{fm(p.amount)}</span>
                          <span className="text-stone-500 ml-2 text-xs">{p.method}</span>
                          <span className="text-stone-500 ml-2 text-xs">{p.date}</span>
                          {p.note && <span className="text-stone-500 ml-2 italic text-xs">— {p.note}</span>}
                        </div>
                        <Button size="icon" variant="ghost" onClick={() => removePayment(p.id)} className="h-7 w-7"><Trash2 className="w-3 h-3 text-red-500" /></Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:items-end border-t border-stone-200 pt-3">
                  <div className="sm:col-span-3">
                    <Label className="text-xs">Metodo</Label>
                    <Select value={paymentForm.method} onValueChange={v => setPaymentForm({ ...paymentForm, method: v })}>
                      <SelectTrigger className="h-9 bg-white border-stone-300" data-testid="pay-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bonifico">Bonifico</SelectItem>
                        <SelectItem value="assegno">Assegno</SelectItem>
                        <SelectItem value="contanti">Contanti</SelectItem>
                        <SelectItem value="carta">Carta</SelectItem>
                        <SelectItem value="altro">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2"><Label className="text-xs">Importo €</Label><Input type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })} className="h-9 bg-white border-stone-300" data-testid="pay-amount" /></div>
                  <div className="sm:col-span-3"><Label className="text-xs">Data</Label><DatePickerIT value={paymentForm.date} onChange={(v) => setPaymentForm({ ...paymentForm, date: v })} data-testid="payment-date-picker" /></div>
                  <div className="sm:col-span-3"><Label className="text-xs">Note</Label><Input value={paymentForm.note} onChange={e => setPaymentForm({ ...paymentForm, note: e.target.value })} placeholder="Rif. ABI/CAB..." className="h-9 bg-white border-stone-300" /></div>
                  <Button className="sm:col-span-1 bg-[#8A1C2A] hover:bg-[#6B1420] text-white h-9 w-full" onClick={addPayment} data-testid="add-payment-btn"><Plus className="w-4 h-4 sm:mr-0 mr-1" /> <span className="sm:hidden">Aggiungi pagamento</span></Button>
                </div>
              </Card>

              <DialogFooter className="flex-wrap gap-2">
                <Button onClick={() => { setEditOrder(selected); setSelected(null); }} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="admin-edit-order-btn">
                  <Pencil className="w-4 h-4 mr-1" /> Modifica ordine
                </Button>
                {["pending_agent", "pending", "confirmed", "postponed", "in_preparation", "ready"].includes(selected.status) && (
                  <Button variant="outline" onClick={() => setChangeCustOpen(true)} className="border-stone-300 text-stone-700" data-testid="admin-change-customer-btn">
                    <Building2 className="w-4 h-4 mr-1" /> Cambia cliente
                  </Button>
                )}
                <Select value={selected.status} onValueChange={(s) => updateStatus(selected.id, s)}>
                  <SelectTrigger className="w-48 bg-white border-stone-300" data-testid="order-status-change"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">In attesa</SelectItem>
                    <SelectItem value="confirmed">Confermato</SelectItem>
                    <SelectItem value="in_preparation">In preparazione</SelectItem>
                    <SelectItem value="ready">Pronto</SelectItem>
                    <SelectItem value="shipped">Spedito</SelectItem>
                    <SelectItem value="delivered">Consegnato</SelectItem>
                    <SelectItem value="completed">Completato</SelectItem>
                    <SelectItem value="cancelled">Annullato</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => deleteOrder(selected.id)} data-testid="delete-order-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Elimina
                </Button>
                <Button variant="outline" onClick={() => setSelected(null)} className="border-stone-300">Chiudi</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Order-scoped chat (opened by row "Chat" button) */}
      <ChatDrawer open={!!chatOrderId} onClose={() => setChatOrderId(null)} initialOrderId={chatOrderId} />
      {editOrder && (
        <OrderEditModal
          order={editOrder}
          onClose={() => setEditOrder(null)}
          onSaved={() => { setEditOrder(null); fetchOrders(); }}
          onOpenChat={(id) => setChatOrderId(id)}
        />
      )}
      {selected && (
        <ChangeCustomerDialog
          order={selected}
          open={changeCustOpen}
          onClose={() => setChangeCustOpen(false)}
          onChanged={() => { setSelected(null); fetchOrders(); }}
        />
      )}
    </div>
  );
};

export default OrdersManagement;
